'use client';

import React from 'react';
import { Download, FileText } from 'lucide-react';
import { BlockProps } from './types';
import { cn } from '@/core/utils/cn';
import { getPublicMediaUrl } from '@/core/utils/media';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { useProductState } from '../ProductContext';

interface Attachment {
    id: number | string;
    title: string;
    file_type?: string | null;
    file_path?: string | null;
    variant_id?: number | null;
}

// Admin canvas only: lets the merchant see and style the block on a template whose preview
// product has no files yet. The live storefront renders nothing in that case.
const DEMO_ATTACHMENTS: Attachment[] = [
    { id: 'demo-manual', title: 'Instrukcja obsługi', file_type: 'pdf' },
    { id: 'demo-datasheet', title: 'Karta techniczna', file_type: 'pdf' },
];

function fileTypeLabel(att: Attachment): string {
    const fromPath = att.file_path ? att.file_path.split('?')[0].split('.').pop() : '';
    return (att.file_type || fromPath || 'pdf').toUpperCase();
}

/** "Pliki do pobrania" — the product's attachments (Specyfikacja tab in the product editor). */
export function ProductDownloadsBlock({ block, currentStyles, isAdmin, product, viewport }: BlockProps) {
    const { t } = useTranslation();
    const productState = useProductState();
    const activeProduct = productState?.product || product;
    const selectedVariantId = productState?.selectedVariant?.id;
    const settings = block.settings || {};

    // Same rule as the default product page: product-wide files plus the chosen variant's own.
    const attachments: Attachment[] = (activeProduct?.attachments || []).filter(
        (att: Attachment) => !att.variant_id || (selectedVariantId && att.variant_id === selectedVariantId)
    );
    const items = attachments.length > 0 ? attachments : (isAdmin ? DEMO_ATTACHMENTS : []);
    if (items.length === 0) return null;

    const showTitle = settings.showTitle !== false;
    const titleText = settings.titleText || t('product.downloads_title', 'Pliki do pobrania');
    const showFileType = settings.showFileType !== false;
    const columns: 1 | 2 = settings.columns === 1 ? 1 : 2;
    const accentColor = settings.accentColor || '#2563eb';
    const textColor = settings.textColor || '#0f172a';
    const cardBgColor = settings.cardBgColor || '#ffffff';
    const cardBorderColor = settings.cardBorderColor || '#e2e8f0';
    const sizeVal = (k: string, fallback: number) => Number(currentStyles?.[k] ?? settings[k] ?? fallback);
    const radius = sizeVal('cardRadius', 12);
    const gap = sizeVal('cardGap', 12);
    const titleSize = sizeVal('titleFontSize', 18);

    // Admin canvas simulates the viewport by scaling a real-width div, so sm: classes never react
    // to the viewport toggle there — pick the column count from `viewport` instead.
    const gridClass = isAdmin
        ? (viewport === 'mobile' || columns === 1 ? 'grid-cols-1' : 'grid-cols-2')
        : (columns === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2');

    return (
        <div style={{ color: textColor, ['--downloads-accent' as string]: accentColor }}>
            {showTitle && (
                <h3 className="font-bold mb-4 flex items-center gap-2" style={{ fontSize: titleSize }}>
                    <FileText className="w-5 h-5 shrink-0" style={{ color: accentColor }} aria-hidden />
                    {titleText}
                </h3>
            )}
            <div className={cn('grid', gridClass)} style={{ gap }}>
                {items.map((att) => (
                    <a
                        key={att.id}
                        href={att.file_path ? getPublicMediaUrl(att.file_path) : '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => { if (isAdmin) e.preventDefault(); }}
                        className="group flex items-center justify-between gap-3 p-3.5 border transition-all hover:shadow-md hover:border-[var(--downloads-accent)]"
                        style={{ backgroundColor: cardBgColor, borderColor: cardBorderColor, borderRadius: radius }}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            <div
                                className="p-2 rounded-lg shrink-0"
                                style={{ color: accentColor, backgroundColor: `color-mix(in srgb, ${accentColor} 10%, transparent)` }}
                            >
                                <FileText className="w-4 h-4" aria-hidden />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold truncate">{att.title}</p>
                                {showFileType && (
                                    <span className="text-[11px] font-medium uppercase opacity-50">{fileTypeLabel(att)}</span>
                                )}
                            </div>
                        </div>
                        <Download className="w-4 h-4 shrink-0 opacity-40 transition-opacity group-hover:opacity-100" style={{ color: accentColor }} aria-hidden />
                    </a>
                ))}
            </div>
        </div>
    );
}
